import { makeAutoObservable, observable, runInAction } from 'mobx'
import { IUserPhoto } from '../models/user_model'
import { API, IWorkspace } from '../services/api'

export type FilesData = Array<IUserPhoto>

const LAST_WORKSPACE_SLUG_KEY = 'lastWorkspaceSlug'

const saveLastWorkspaceSlug = (slug: string) => {
  try {
    localStorage.setItem(LAST_WORKSPACE_SLUG_KEY, slug)
  } catch {}
}

const getLastWorkspaceSlug = (): string | null => {
  try {
    return localStorage.getItem(LAST_WORKSPACE_SLUG_KEY)
  } catch {
    return null
  }
}

export const clearLastWorkspaceSlug = () => {
  try {
    localStorage.removeItem(LAST_WORKSPACE_SLUG_KEY)
  } catch {}
}

export class WorkspacesStore {
  workspaces: IWorkspace[] = []
  activeWorkspaceId?: string

  constructor() {
    makeAutoObservable(this)
  }

  get activeWorkspace(): IWorkspace | undefined {
    return this.workspaces.find((e) => e.id === this.activeWorkspaceId)
  }

  get activeWorkspaceSlug(): string | undefined {
    return this.activeWorkspace?.slug
  }

  reset = () => {
    this.workspaces = []
  }

  fetch = async (currentSlugOrId?: string) => {
    const response = await API.getInstance().getWorkspaces()
    runInAction(() => {
      this.workspaces = []
      if (response.ok) {
        this.workspaces = [...response.data]
      }

      // Try to resolve the provided slug or ID
      const resolved = currentSlugOrId
        ? this.getWorkspaceBySlugOrId(currentSlugOrId)
        : undefined

      if (resolved) {
        this.activeWorkspaceId = resolved.id
        saveLastWorkspaceSlug(resolved.slug)
      } else {
        // Priority: saved slug from localStorage → private workspace (name === null) → first
        const savedSlug = getLastWorkspaceSlug()
        const savedWorkspace = savedSlug
          ? this.workspaces.find((w) => w.slug === savedSlug)
          : undefined
        const privateWorkspace = this.workspaces.find((e) => e.name === null)
        const fallback = savedWorkspace || privateWorkspace || this.workspaces[0]

        this.activeWorkspaceId = fallback?.id
        if (fallback) saveLastWorkspaceSlug(fallback.slug)
      }
    })
  }

  setActiveWorkspace = (workspace: IWorkspace) => {
    const found = this.workspaces.find((e) => e.id === workspace.id)
    if (found) {
      this.activeWorkspaceId = found.id
      saveLastWorkspaceSlug(found.slug)
    }
  }

  getWorkspaceById = (workspaceId: string): IWorkspace | undefined =>
    this.workspaces.find((e) => e.id === workspaceId)

  getWorkspaceBySlug = (slug: string): IWorkspace | undefined =>
    this.workspaces.find((e) => e.slug === slug)

  getWorkspaceBySlugOrId = (value: string): IWorkspace | undefined =>
    this.workspaces.find((e) => e.slug === value) ||
    this.workspaces.find((e) => e.id === value)
}
